import ClickAwayListener from '@mui/material/ClickAwayListener';
import React from 'react';
import ReactDOM from 'react-dom';
import { connect, DispatchProp } from 'react-redux';
import { DOWN, SwipeEventData, UP } from 'react-swipeable';

import { Tooltip } from '@dndbeyond/character-common-components/es';
import {
    DisabledPaneLeftSvg,
    DisabledPaneRightSvg,
    LightLockSvg,
    LightSidebarAlignLeftSvg,
    LightSidebarAlignRightSvg,
    LightSidebarFixedPositionSvg,
    LightSidebarLeftSvg,
    LightSidebarOverlayPositionSvg,
    LightSidebarRightSvg,
    LightUnlockSvg,
    ThemedDarkSidebarCapSvg,
    ThemedPaneLeftSvg,
    ThemedPaneRightSvg,
    ThemedSidebarCapSvg,
} from '@dndbeyond/character-components/es';
import {
    CharacterTheme,
    RuleData,
    rulesEngineSelectors,
    syncTransactionSelectors,
} from '@dndbeyond/character-rules-engine/es';

import { Swipeable } from '~/components/Swipeable';

import { DESKTOP_COMPONENT_START_WIDTH, SIDEBAR_FIXED_POSITION_START_WIDTH } from '../../../CharacterSheet/config';
import { sidebarActions } from '../../actions';
import { Sidebar as SidebarComponent } from '../../components/Sidebar';
import { ThemeModeEnum } from '../../constants/App';
import { appEnvSelectors, sidebarSelectors } from '../../selectors';
import { AppEnvDimensionsState, SidebarPaneComponentInfoState, SidebarPaneState } from '../../stores/typings';
import { TippyOpts } from '../../typings';
import { PaneComponentEnum, SidebarAlignmentEnum, SidebarPlacementEnum } from '../../utils';
import { PaneComponentProperties } from '../../utils/Pane';
import AbilityPane from '../panes/AbilityPane';
import AbilitySavingThrowsPane from '../panes/AbilitySavingThrowsPane';
import ActionPane from '../panes/ActionPane';
import ArmorManagePane from '../panes/ArmorManagePane';
import BackgroundPane from '../panes/BackgroundPane';
import BasicActionPane from '../panes/BasicActionPane';
import CampaignPane from '../panes/CampaignPane';
import CharacterManagePane from '../panes/CharacterManagePane';
import CharacterSpellPane from '../panes/CharacterSpellPane';
import ClassFeaturePane from '../panes/ClassFeaturePane';
import ClassSpellPane from '../panes/ClassSpellPane';
import ConditionManagePane from '../panes/ConditionManagePane';
import ContainerPane from '../panes/ContainerPane';
import CreaturePane from '../panes/CreaturePane';
import CurrencyPane from '../panes/CurrencyPane';
import CustomActionPane from '../panes/CustomActionPane';
import CustomActionsPane from '../panes/CustomActionsPane';
import CustomSkillPane from '../panes/CustomSkillPane';
import { DecoratePane } from '../panes/DecoratePane';
import DefenseManagePane from '../panes/DefenseManagePane';
import DescriptionPane from '../panes/DescriptionPane';
import EncumbrancePane from '../panes/EncumbrancePane';
import EquipmentManagePane from '../panes/EquipmentManagePane';
import ExportPdfPane from '../panes/ExportPdfPane';
import ExtraManagePane from '../panes/ExtraManagePane';
import FeatPane from '../panes/FeatPane';
import FeatsManagePane from '../panes/FeatsManagePane';
import { GameLogPane } from '../panes/GameLogPane';
import HealthManagePane from '../panes/HealthManagePane';
import InfusionChoicePane from '../panes/InfusionChoicePane';
import InitiativePane from '../panes/InitiativePane';
import InspirationPane from '../panes/InspirationPane';
import ItemPane from '../panes/ItemPane';
import LongRestPane from '../panes/LongRestPane';
import NoteManagePane from '../panes/NoteManagePane';
import PreferencesHitPointConfirmPane from '../panes/PreferencesHitPointConfirmPane';
import PreferencesOptionalClassFeaturesConfirmPane from '../panes/PreferencesOptionalClassFeaturesConfirmPane';
import PreferencesOptionalOriginsConfirmPane from '../panes/PreferencesOptionalOriginsConfirmPane';
import PreferencesPane from '../panes/PreferencesPane';
import PreferencesProgressionConfirmPane from '../panes/PreferencesProgressionConfirmPane';
import ProficienciesPane from '../panes/ProficienciesPane';
import ProficiencyBonusPane from '../panes/ProficiencyBonusPane';
import RacialTraitPane from '../panes/RacialTraitPane';
import SavingThrowsPane from '../panes/SavingThrowsPane';
import SenseManagePane from '../panes/SenseManagePane';
import SettingsPane from '../panes/SettingsPane';
import ShareUrlPane from '../panes/ShareUrlPane';
import ShortRestPane from '../panes/ShortRestPane';
import SkillPane from '../panes/SkillPane';
import SkillsPane from '../panes/SkillsPane';
import SpeedManagePane from '../panes/SpeedManagePane';
import SpellManagePane from '../panes/SpellManagePane';
import StartingEquipmentPane from '../panes/StartingEquipmentPane';
import TraitPane from '../panes/TraitPane';
import VehicleComponentPane from '../panes/VehicleComponentPane';
import VehiclePane from '../panes/VehiclePane';
import XpPane from '../panes/XpPane';

type SidebarPaneComponent<T> = React.ComponentType<T> & PaneComponentProperties;
interface Props extends DispatchProp {
    isVisible: boolean;
    isLocked: boolean;
    pane?: SidebarPaneState;
    placement: string;
    alignment: string;
    ruleData: RuleData;
    envDimensions: AppEnvDimensionsState;
    style: React.CSSProperties;
    isSyncActive: boolean;
    theme: CharacterTheme;
    setSwipedAmount?: (swipedAmount: number) => void;
}
class Sidebar extends React.PureComponent<Props, {}> {
    static defaultProps = {
        isSyncActive: false,
    };

    isHandlingDocumentClick: boolean = false;
    portal: HTMLDivElement;

    constructor(props: Props) {
        super(props);
        this.portal = document.createElement('div');
        this.portal.classList.add('ct-sidebar__portal');
        document.body.classList.add(`ct-sidebar__body--${props.placement}-${props.alignment}`);
        document.body.classList.add(`ct-sidebar__body--${props.isVisible ? 'visible' : 'hidden'}`);
        document.body.classList.add(`ct-sidebar__body--${props.isVisible ? 'visible' : 'hidden'}-${props.placement}`);

        props.dispatch(sidebarActions.paneCreate());
    }

    componentDidMount() {
        document.body.appendChild(this.portal);
    }

    componentWillUnmount() {
        document.body.removeChild(this.portal);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<{}>, snapshot?: any): void {
        const { isVisible, placement, alignment } = this.props;

        if (isVisible !== prevProps.isVisible) {
            document.body.classList.remove(`ct-sidebar__body--${prevProps.isVisible ? 'visible' : 'hidden'}`);
            document.body.classList.add(`ct-sidebar__body--${isVisible ? 'visible' : 'hidden'}`);
        }

        if (isVisible !== prevProps.isVisible || placement !== prevProps.placement) {
            document.body.classList.remove(
                `ct-sidebar__body--${prevProps.isVisible ? 'visible' : 'hidden'}-${prevProps.alignment}`,
            );
            document.body.classList.add(`ct-sidebar__body--${isVisible ? 'visible' : 'hidden'}-${alignment}`);
        }

        if (placement !== prevProps.placement || alignment !== prevProps.alignment) {
            document.body.classList.remove(`ct-sidebar__body--${prevProps.placement}-${prevProps.alignment}`);
            document.body.classList.add(`ct-sidebar__body--${placement}-${alignment}`);
        }
    }

    handleToggleVisibility = (): void => {
        const { dispatch, isVisible, setSwipedAmount } = this.props;
        if (isVisible && setSwipedAmount) {
            setSwipedAmount(0);
        }
        dispatch(sidebarActions.visibleSet(!isVisible));
    };

    handleToggleLock = (): void => {
        const { dispatch, isLocked } = this.props;

        dispatch(sidebarActions.lockSet(!isLocked));
    };

    handleToggleAlignment = (): void => {
        const { dispatch, alignment } = this.props;

        let newAlignment: SidebarAlignmentEnum =
            alignment === SidebarAlignmentEnum.RIGHT ? SidebarAlignmentEnum.LEFT : SidebarAlignmentEnum.RIGHT;

        dispatch(sidebarActions.alignmentSet(newAlignment));
    };

    handleTogglePlacement = (): void => {
        const { dispatch, placement } = this.props;

        let newPlacement: SidebarPlacementEnum =
            placement === SidebarPlacementEnum.OVERLAY ? SidebarPlacementEnum.FIXED : SidebarPlacementEnum.OVERLAY;

        dispatch(sidebarActions.placementSet(newPlacement));
    };

    handlePaneHistoryPrevious = (): void => {
        const { dispatch } = this.props;
        dispatch(sidebarActions.paneHistoryPrevious());
    };

    handlePaneHistoryNext = (): void => {
        const { dispatch } = this.props;
        dispatch(sidebarActions.paneHistoryNext());
    };

    getThemeMode = (): ThemeModeEnum => {
        const { theme } = this.props;

        //Default
        let themeMode: ThemeModeEnum = ThemeModeEnum.LIGHT;

        //CharacterTheme
        if (theme.isDarkMode) {
            themeMode = ThemeModeEnum.DARK;
        }

        //Active Pane theme override
        const activePaneComponentThemeMode: ThemeModeEnum | null = this.getActiveEntryComponent()?.themeMode ?? null;
        if (activePaneComponentThemeMode) {
            themeMode = activePaneComponentThemeMode;
        }
        return themeMode;
    };

    isActiveEntryFullWidth = (): boolean => {
        return this.getActiveEntryComponent()?.isFullWidth ?? false;
    };

    getActiveEntry = (): SidebarPaneComponentInfoState | null => {
        const { pane } = this.props;

        if (pane === undefined) {
            return null;
        }

        const { historyIdx, history } = pane;

        if (historyIdx === null) {
            return null;
        }

        return history[historyIdx] ?? null;
    };

    getActiveEntryComponent = (): SidebarPaneComponent<any> | null => {
        let SidebarPaneComponent: SidebarPaneComponent<any> | null = null;
        let activeEntry = this.getActiveEntry();

        switch (activeEntry?.componentType) {
            case PaneComponentEnum.HEALTH_MANAGE:
                SidebarPaneComponent = HealthManagePane;
                break;

            case PaneComponentEnum.ITEM_DETAIL:
                SidebarPaneComponent = ItemPane;
                break;

            case PaneComponentEnum.CHARACTER_SPELL_DETAIL:
                SidebarPaneComponent = CharacterSpellPane;
                break;

            case PaneComponentEnum.CLASS_SPELL_DETAIL:
                SidebarPaneComponent = ClassSpellPane;
                break;

            case PaneComponentEnum.ABILITY:
                SidebarPaneComponent = AbilityPane;
                break;

            case PaneComponentEnum.SKILLS:
                SidebarPaneComponent = SkillsPane;
                break;

            case PaneComponentEnum.PROFICIENCIES:
                SidebarPaneComponent = ProficienciesPane;
                break;

            case PaneComponentEnum.XP:
                SidebarPaneComponent = XpPane;
                break;

            case PaneComponentEnum.SHORT_REST:
                SidebarPaneComponent = ShortRestPane;
                break;

            case PaneComponentEnum.LONG_REST:
                SidebarPaneComponent = LongRestPane;
                break;

            case PaneComponentEnum.SPELL_MANAGE:
                SidebarPaneComponent = SpellManagePane;
                break;

            case PaneComponentEnum.CONDITION_MANAGE:
                SidebarPaneComponent = ConditionManagePane;
                break;

            case PaneComponentEnum.SPEED_MANAGE:
                SidebarPaneComponent = SpeedManagePane;
                break;

            case PaneComponentEnum.SENSE_MANAGE:
                SidebarPaneComponent = SenseManagePane;
                break;

            case PaneComponentEnum.DEFENSE_MANAGE:
                SidebarPaneComponent = DefenseManagePane;
                break;

            case PaneComponentEnum.ARMOR_MANAGE:
                SidebarPaneComponent = ArmorManagePane;
                break;

            case PaneComponentEnum.FEATS_MANAGE:
                SidebarPaneComponent = FeatsManagePane;
                break;

            case PaneComponentEnum.NOTE_MANAGE:
                SidebarPaneComponent = NoteManagePane;
                break;

            case PaneComponentEnum.TRAIT:
                SidebarPaneComponent = TraitPane;
                break;

            case PaneComponentEnum.CAMPAIGN:
                SidebarPaneComponent = CampaignPane;
                break;

            case PaneComponentEnum.EQUIPMENT_MANAGE:
                SidebarPaneComponent = EquipmentManagePane;
                break;

            case PaneComponentEnum.CURRENCY:
                SidebarPaneComponent = CurrencyPane;
                break;

            case PaneComponentEnum.ENCUMBRANCE:
                SidebarPaneComponent = EncumbrancePane;
                break;

            case PaneComponentEnum.STARTING_EQUIPMENT:
                SidebarPaneComponent = StartingEquipmentPane;
                break;

            case PaneComponentEnum.CUSTOM_ACTIONS:
                SidebarPaneComponent = CustomActionsPane;
                break;

            case PaneComponentEnum.CUSTOM_ACTION:
                SidebarPaneComponent = CustomActionPane;
                break;

            case PaneComponentEnum.CLASS_FEATURE_DETAIL:
                SidebarPaneComponent = ClassFeaturePane;
                break;

            case PaneComponentEnum.FEAT_DETAIL:
                SidebarPaneComponent = FeatPane;
                break;

            case PaneComponentEnum.RACIAL_TRAIT_DETAIL:
                SidebarPaneComponent = RacialTraitPane;
                break;

            case PaneComponentEnum.BACKGROUND:
                SidebarPaneComponent = BackgroundPane;
                break;

            case PaneComponentEnum.DESCRIPTION:
                SidebarPaneComponent = DescriptionPane;
                break;

            case PaneComponentEnum.PROFICIENCY_BONUS:
                SidebarPaneComponent = ProficiencyBonusPane;
                break;

            case PaneComponentEnum.INSPIRATION:
                SidebarPaneComponent = InspirationPane;
                break;

            case PaneComponentEnum.INITIATIVE:
                SidebarPaneComponent = InitiativePane;
                break;

            case PaneComponentEnum.ACTION:
                SidebarPaneComponent = ActionPane;
                break;

            case PaneComponentEnum.PREFERENCES:
                SidebarPaneComponent = PreferencesPane;
                break;

            case PaneComponentEnum.SKILL:
                SidebarPaneComponent = SkillPane;
                break;

            case PaneComponentEnum.CUSTOM_SKILL:
                SidebarPaneComponent = CustomSkillPane;
                break;

            case PaneComponentEnum.SAVING_THROWS:
                SidebarPaneComponent = SavingThrowsPane;
                break;

            case PaneComponentEnum.ABILITY_SAVING_THROW:
                SidebarPaneComponent = AbilitySavingThrowsPane;
                break;

            case PaneComponentEnum.BASIC_ACTION:
                SidebarPaneComponent = BasicActionPane;
                break;

            case PaneComponentEnum.PREFERENCES_HIT_POINT_CONFIRM:
                SidebarPaneComponent = PreferencesHitPointConfirmPane;
                break;

            case PaneComponentEnum.PREFERENCES_OPTIONAL_CLASS_FEATURES_CONFIRM:
                SidebarPaneComponent = PreferencesOptionalClassFeaturesConfirmPane;
                break;

            case PaneComponentEnum.PREFERENCES_OPTIONAL_ORIGINS_CONFIRM:
                SidebarPaneComponent = PreferencesOptionalOriginsConfirmPane;
                break;

            case PaneComponentEnum.PREFERENCES_PROGRESSION_CONFIRM:
                SidebarPaneComponent = PreferencesProgressionConfirmPane;
                break;

            case PaneComponentEnum.SHARE_URL:
                SidebarPaneComponent = ShareUrlPane;
                break;

            case PaneComponentEnum.CREATURE:
                SidebarPaneComponent = CreaturePane;
                break;

            case PaneComponentEnum.VEHICLE:
                SidebarPaneComponent = VehiclePane;
                break;

            case PaneComponentEnum.VEHICLE_COMPONENT:
                SidebarPaneComponent = VehicleComponentPane;
                break;

            case PaneComponentEnum.EXTRA_MANAGE:
                SidebarPaneComponent = ExtraManagePane;
                break;

            case PaneComponentEnum.INFUSION_CHOICE:
                SidebarPaneComponent = InfusionChoicePane;
                break;

            case PaneComponentEnum.EXPORT_PDF:
                SidebarPaneComponent = ExportPdfPane;
                break;

            case PaneComponentEnum.CHARACTER_MANAGE:
                SidebarPaneComponent = CharacterManagePane;
                break;

            case PaneComponentEnum.GAME_LOG:
                SidebarPaneComponent = GameLogPane;
                break;

            case PaneComponentEnum.CONTAINER:
                SidebarPaneComponent = ContainerPane;
                break;

            case PaneComponentEnum.DECORATE:
                SidebarPaneComponent = DecoratePane;
                break;

            case PaneComponentEnum.SETTINGS:
                SidebarPaneComponent = SettingsPane;
                break;
        }

        return SidebarPaneComponent;
    };

    renderComponent = (): React.ReactNode => {
        const { theme } = this.props;

        let activeEntry = this.getActiveEntry();
        if (activeEntry === null) {
            return <div>Component history failed to initialize.</div>;
        }

        const SidebarPaneComponent = this.getActiveEntryComponent();

        if (SidebarPaneComponent !== null) {
            return (
                <SidebarPaneComponent
                    {...activeEntry.componentProps}
                    theme={theme}
                    identifiers={activeEntry.componentIdentifiers}
                />
            );
        }

        return <div>Component was not found.</div>;
    };

    renderPaneControls = (): React.ReactNode => {
        const { pane, theme } = this.props;

        if (pane === undefined) {
            return null;
        }

        const { historyIdx, history } = pane;

        if (history.length <= 1) {
            return null;
        }

        const isActiveIndexAtStart: boolean = historyIdx === 0;
        const isActiveIndexAtEnd: boolean = historyIdx === history.length - 1;

        let PrevIcon: React.ComponentType<any> = isActiveIndexAtStart ? DisabledPaneLeftSvg : ThemedPaneLeftSvg;
        let NextIcon: React.ComponentType<any> = isActiveIndexAtEnd ? DisabledPaneRightSvg : ThemedPaneRightSvg;

        const themeMode = this.getThemeMode();
        const paneControlClassNames: Array<string> = ['ct-sidebar__pane-control'];
        if (themeMode === ThemeModeEnum.DARK) {
            paneControlClassNames.push('ct-sidebar__pane-control--is-dark-mode');
        }

        const prevPaneControlClassNames: Array<string> = [...paneControlClassNames, 'ct-sidebar__pane-control--prev'];
        if (isActiveIndexAtStart) {
            prevPaneControlClassNames.push('ct-sidebar__pane-control--disabled');
        }

        const nextPaneControlClassNames: Array<string> = [...paneControlClassNames, 'ct-sidebar__pane-control--next'];
        if (isActiveIndexAtEnd) {
            nextPaneControlClassNames.push('ct-sidebar__pane-control--disabled');
        }

        return (
            <div className="ct-sidebar__pane-controls">
                <span
                    className={prevPaneControlClassNames.join(' ')}
                    onClick={isActiveIndexAtStart ? undefined : this.handlePaneHistoryPrevious}
                >
                    <PrevIcon className="ct-sidebar__pane-control-icon" theme={theme} />
                    <span className="ct-sidebar__pane-control-label">Prev</span>
                </span>
                <span
                    className={nextPaneControlClassNames.join(' ')}
                    onClick={isActiveIndexAtEnd ? undefined : this.handlePaneHistoryNext}
                >
                    <span className="ct-sidebar__pane-control-label">Next</span>
                    <NextIcon className="ct-sidebar__pane-control-icon" theme={theme} />
                </span>
            </div>
        );
    };

    renderEmptyPane = (): React.ReactNode => {
        return (
            <div className="ct-sidebar__pane-default">
                Select elements on the character sheet to display more information about them.
            </div>
        );
    };

    renderPaneContent = (entry: any): React.ReactNode => {
        return (
            <React.Fragment>
                {this.renderPaneControls()}
                {this.renderComponent()}
            </React.Fragment>
        );
    };

    renderActivePane = (): React.ReactNode => {
        const { pane, isVisible, envDimensions, theme } = this.props;

        if (!pane) {
            return null;
        }

        if (envDimensions.window.width >= DESKTOP_COMPONENT_START_WIDTH) {
            if (!isVisible) {
                return null;
            }
        }

        let activeEntry = this.getActiveEntry();
        const isActiveEntryFullWidth = this.isActiveEntryFullWidth();
        const activeEntryThemeMode = this.getThemeMode();
        const isDarkMode: boolean = activeEntryThemeMode === ThemeModeEnum.DARK;

        const SidebarCap: React.ComponentType<any> = isDarkMode ? ThemedDarkSidebarCapSvg : ThemedSidebarCapSvg;

        let paneContentClassNames: Array<string> = ['ct-sidebar__pane-content'];
        if (isActiveEntryFullWidth) {
            paneContentClassNames.push('ct-sidebar__pane-content--is-full-width');
        }
        let paneGapClassNames: Array<string> = ['ct-sidebar__pane-gap'];
        if (isDarkMode) {
            paneContentClassNames.push('ct-sidebar__pane-content--is-dark-mode');
            paneGapClassNames.push('ct-sidebar__pane-gap--is-dark-mode');
        }

        return (
            <div className="ct-sidebar__pane">
                <div className="ct-sidebar__pane-top">
                    <SidebarCap theme={theme} />
                </div>
                <div className={`${paneGapClassNames.join(' ')} ct-sidebar__pane-gap--top`} />
                <div className={paneContentClassNames.join(' ')}>
                    {activeEntry ? this.renderPaneContent(activeEntry) : this.renderEmptyPane()}
                </div>
                <div className={`${paneGapClassNames.join(' ')} ct-sidebar__pane-gap--bottom`} />
                <div className="ct-sidebar__pane-bottom">
                    <SidebarCap theme={theme} />
                </div>
            </div>
        );
    };

    tooltipOptions = (): TippyOpts => {
        return {
            dynamicTitle: true,
        };
    };

    renderVisibilityControl = (): React.ReactNode => {
        const { isVisible, alignment, theme } = this.props;

        let VisiblityIcon: React.ComponentType<any> | null = null;

        if (isVisible) {
            if (alignment === SidebarAlignmentEnum.LEFT) {
                VisiblityIcon = LightSidebarLeftSvg;
            } else if (alignment === SidebarAlignmentEnum.RIGHT) {
                VisiblityIcon = LightSidebarRightSvg;
            }
        } else {
            if (alignment === SidebarAlignmentEnum.LEFT) {
                VisiblityIcon = LightSidebarRightSvg;
            } else if (alignment === SidebarAlignmentEnum.RIGHT) {
                VisiblityIcon = LightSidebarLeftSvg;
            }
        }

        if (!VisiblityIcon) {
            return null;
        }

        return (
            <Tooltip
                isDarkMode={theme.isDarkMode}
                className={`ct-sidebar__control ct-sidebar__control--${isVisible ? 'collapse' : 'expand'}`}
                onClick={this.handleToggleVisibility}
                title={isVisible ? 'Hide Sidebar' : 'Show Sidebar'}
                tippyOpts={this.tooltipOptions()}
                isInteractive={true}
            >
                <VisiblityIcon className="ct-sidebar__control-icon" />
            </Tooltip>
        );
    };

    renderLockControl = (): React.ReactNode => {
        const { isLocked, theme } = this.props;

        let LockIcon: React.ComponentType<any> = isLocked ? LightLockSvg : LightUnlockSvg;

        return (
            <Tooltip
                isDarkMode={theme.isDarkMode}
                className={`ct-sidebar__control ct-sidebar__control--${isLocked ? 'lock' : 'unlock'}`}
                onClick={this.handleToggleLock}
                title={isLocked ? 'Locked' : 'Unlocked'}
                tippyOpts={this.tooltipOptions()}
                isInteractive={true}
            >
                <LockIcon className="ct-sidebar__control-icon" />
            </Tooltip>
        );
    };

    renderControls = (): React.ReactNode => {
        const { isVisible, isLocked, alignment, placement, envDimensions, theme } = this.props;

        if (isVisible) {
            return (
                <div className="ct-sidebar__controls">
                    {!isLocked && (
                        <span className="ct-sidebar__control-group ct-sidebar__control-group--visibility">
                            {this.renderVisibilityControl()}
                        </span>
                    )}
                    <span className="ct-sidebar__control-group ct-sidebar__control-group--lock">
                        {this.renderLockControl()}
                    </span>
                    {!isLocked && (
                        <React.Fragment>
                            {envDimensions.window.width > SIDEBAR_FIXED_POSITION_START_WIDTH && (
                                <span className="ct-sidebar__control-group ct-sidebar__control-group--placement">
                                    <Tooltip
                                        isDarkMode={theme.isDarkMode}
                                        className={`ct-sidebar__control ct-sidebar__control--overlay ${
                                            placement === SidebarPlacementEnum.OVERLAY
                                                ? ''
                                                : 'ct-sidebar__control--overlay-unselected'
                                        }`}
                                        onClick={
                                            placement === SidebarPlacementEnum.OVERLAY
                                                ? undefined
                                                : this.handleTogglePlacement
                                        }
                                        title={'Overlay'}
                                        tippyOpts={this.tooltipOptions()}
                                        isInteractive={placement === SidebarPlacementEnum.FIXED}
                                    >
                                        <LightSidebarOverlayPositionSvg className="ct-sidebar__control-icon" />
                                    </Tooltip>
                                    <Tooltip
                                        isDarkMode={theme.isDarkMode}
                                        className={`ct-sidebar__control ct-sidebar__control--fixed ${
                                            placement === SidebarPlacementEnum.FIXED
                                                ? ''
                                                : 'ct-sidebar__control--fixed-unselected'
                                        }`}
                                        onClick={
                                            placement === SidebarPlacementEnum.FIXED
                                                ? undefined
                                                : this.handleTogglePlacement
                                        }
                                        title={'Fixed'}
                                        tippyOpts={this.tooltipOptions()}
                                        isInteractive={placement === SidebarPlacementEnum.OVERLAY}
                                    >
                                        <LightSidebarFixedPositionSvg className="ct-sidebar__control-icon" />
                                    </Tooltip>
                                </span>
                            )}
                            <span className="ct-sidebar__control-group ct-sidebar__control-group--alignment">
                                <Tooltip
                                    isDarkMode={theme.isDarkMode}
                                    className={`ct-sidebar__control ct-sidebar__control--left ${
                                        alignment === SidebarAlignmentEnum.LEFT
                                            ? ''
                                            : 'ct-sidebar__control--left-unselected'
                                    }`}
                                    onClick={
                                        alignment === SidebarAlignmentEnum.LEFT ? undefined : this.handleToggleAlignment
                                    }
                                    title={'Align Left'}
                                    tippyOpts={this.tooltipOptions()}
                                    isInteractive={alignment === SidebarAlignmentEnum.RIGHT}
                                >
                                    <LightSidebarAlignLeftSvg className="ct-sidebar__control-icon" />
                                </Tooltip>
                                <Tooltip
                                    isDarkMode={theme.isDarkMode}
                                    className={`ct-sidebar__control ct-sidebar__control--right ${
                                        alignment === SidebarAlignmentEnum.RIGHT
                                            ? ''
                                            : 'ct-sidebar__control--right-unselected'
                                    }`}
                                    onClick={
                                        alignment === SidebarAlignmentEnum.RIGHT
                                            ? undefined
                                            : this.handleToggleAlignment
                                    }
                                    title={'Align Right'}
                                    tippyOpts={this.tooltipOptions()}
                                    isInteractive={alignment === SidebarAlignmentEnum.LEFT}
                                >
                                    <LightSidebarAlignRightSvg className="ct-sidebar__control-icon" />
                                </Tooltip>
                            </span>
                        </React.Fragment>
                    )}
                </div>
            );
        }

        return <div className="ct-sidebar__controls">{this.renderVisibilityControl()}</div>;
    };

    renderMask = (): React.ReactNode => {
        const { isVisible, theme } = this.props;

        let classNames: Array<string> = ['ct-sidebar__mask', `ct-sidebar__mask--${isVisible ? 'visible' : 'hidden'}`];

        if (theme.isDarkMode) {
            classNames.push('ct-sidebar__mask--dark-mode');
        }

        return <div className={classNames.join(' ')} />;
    };
    handleSwiping = ({ deltaX, dir }: SwipeEventData): void => {
        const isVerticalChange = [UP, DOWN].includes(dir);
        if (!isVerticalChange && this.props.setSwipedAmount) {
            this.props.setSwipedAmount(deltaX);
        }
    };

    renderPortal = () => {
        const { isSyncActive, isVisible, alignment, style, isLocked, placement } = this.props;

        return (
            <React.Fragment>
                {this.renderMask()}
                <ClickAwayListener
                    onClickAway={() => {
                        if (isVisible && !isLocked && placement !== SidebarPlacementEnum.FIXED) {
                            this.handleToggleVisibility();
                        }
                    }}
                    touchEvent={false}
                >
                    <div>
                        <Swipeable
                            handlerFunctions={{
                                onSwipedRight: this.handleToggleVisibility,
                                onSwiping: this.handleSwiping,
                            }}
                        >
                            <SidebarComponent
                                alignment={alignment}
                                isVisible={isVisible}
                                style={style}
                                isSyncActive={isSyncActive}
                                themeMode={this.getThemeMode()}
                                isFullWidth={this.isActiveEntryFullWidth()}
                            >
                                {this.renderControls()}
                                {this.renderActivePane()}
                            </SidebarComponent>
                        </Swipeable>
                    </div>
                </ClickAwayListener>
            </React.Fragment>
        );
    };

    render() {
        return ReactDOM.createPortal(this.renderPortal(), this.portal);
    }
}

function mapStateToProps(state) {
    return {
        isVisible: sidebarSelectors.getIsVisible(state),
        isLocked: sidebarSelectors.getIsLocked(state),
        pane: sidebarSelectors.getActivePane(state),
        placement: sidebarSelectors.getPlacement(state),
        alignment: sidebarSelectors.getAlignment(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        envDimensions: appEnvSelectors.getDimensions(state),
        isSyncActive: syncTransactionSelectors.getActive(state),
        theme: rulesEngineSelectors.getCharacterTheme(state),
    };
}

export default connect(mapStateToProps)(Sidebar);
